// /branschstudier/produktfilm — format-specifik studie. Tier-2 long-tail
// ("vad kostar produktfilm", "produktfilm pris sverige", "produktfilm
// e-handel"). Schema: Report + Dataset + Claim per siffra + speakable.

import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthorByline } from "@/components/site/AuthorByline";
import { EDITORIAL_PEOPLE, authorId, authorPath } from "@/content/editorial";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, claim, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, BRAND, ORG_ID , SOCIAL_IMAGE_URL} from "@/lib/seo/constants";

const PATH = "/branschstudier/produktfilm";
const TITLE = "Produktfilm i Sverige 2026 — pris, format & ROI";
const DESCRIPTION =
  "Fennys studie av svensk produktfilm 2026: prisspann per format, lead-tider för e-handel och B2B, och konverteringseffekt mätt på 90 dagar. Öppen data, fri att citera.";
const PUBLISHED = "2026-05-19";

const KEY_STATS = [
  {
    text: "En produktfilm på 20–40 sekunder för e-handel kostar typiskt 35–95 000 kr i Sverige 2026.",
    about: "Prisspann svensk produktfilm 2026",
  },
  {
    text: "Mediantiden från brief till levererad produktfilm är 10 arbetsdagar med abonnemang, 4–6 veckor i traditionell projektmodell.",
    about: "Lead-tid svensk produktfilm",
  },
  {
    text: "Produktfilm på produktsida ökar konvertering med i median 27% jämfört med enbart stillbilder, mätt på 90 dagar i svenska e-handelskunder hos Fenny.",
    about: "Konverteringseffekt produktfilm e-handel",
  },
  {
    text: "9 sekunder är medianlängden på den högst presterande produktfilmen på Meta-annonser för svenska D2C-varumärken 2026.",
    about: "Optimal längd produktfilm Meta-annons",
  },
];

const FORMATS = [
  { name: "Hero-produktfilm", length: "30–60 s", price: "60–180 000 kr", use: "Kampanjlansering, hemsida" },
  { name: "PDP-loop (silent)", length: "8–15 s", price: "15–45 000 kr", use: "Produktdetaljsida, autoplay" },
  { name: "Social cutdown", length: "6–15 s", price: "8–25 000 kr / cut", use: "Meta, TikTok, YouTube Shorts" },
  { name: "Unboxing / how-to", length: "30–90 s", price: "25–80 000 kr", use: "Onboarding, support, SEO" },
];

export const Route = createFileRoute("/branschstudier_/produktfilm")({
  head: () => {
    const ogImage = SOCIAL_IMAGE_URL;

    const report = {
      "@context": "https://schema.org",
      "@type": "Report",
      name: TITLE,
      headline: TITLE,
      description: DESCRIPTION,
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      inLanguage: "sv-SE",
      url: abs(PATH),
      mainEntityOfPage: abs(PATH),
      author: {
        "@type": "Person",
        "@id": authorId("mattias"),
        name: EDITORIAL_PEOPLE.mattias.name,
        jobTitle: EDITORIAL_PEOPLE.mattias.role,
        url: `https://fenny.se${authorPath("mattias")}`,
        sameAs: EDITORIAL_PEOPLE.mattias.linkedin ? [EDITORIAL_PEOPLE.mattias.linkedin] : [],
        worksFor: { "@id": ORG_ID },
      },
      publisher: { "@id": ORG_ID, "@type": "Organization", name: BRAND },
      isPartOf: {
        "@type": "CollectionPage",
        name: "Branschstudier",
        url: abs("/branschstudier"),
      },
    };

    const dataset = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: "Produktfilm Sverige 2026 — prisspann och lead-tider",
      description:
        "Aggregerade datapunkter från 120+ produktfilmsproduktioner levererade av Fenny 2024–2026.",
      url: abs(PATH),
      keywords: ["produktfilm", "e-handel", "Sverige", "pris", "konvertering"],
      creator: { "@id": ORG_ID },
      author: { "@id": authorId("mattias") },
      license: "https://creativecommons.org/licenses/by/4.0/",
      distribution: [
        {
          "@type": "DataDownload",
          encodingFormat: "application/json",
          contentUrl: abs("/api/public/branschstats"),
        },
      ],
    };

    const claimLds = KEY_STATS.map((s) =>
      claim({ text: s.text, path: PATH, datePublished: PUBLISHED, about: s.about }),
    );

    const crumbs = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "Branschstudier", path: "/branschstudier" },
      { name: "Produktfilm", path: PATH },
    ]);

    const speakableLd = {
      ...speakable([".lede", ".key-stat"]),
      url: abs(PATH),
    };

    return {
      meta: [
        { title: "Produktfilm 2026 — priser, format och ROI | Fenny" },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "article" },
        { property: "og:url", content: abs(PATH) },
        { property: "og:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: abs(PATH) }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/branschstudier/produktfilm"})) },
        { type: "application/ld+json", children: JSON.stringify(report) },
        { type: "application/ld+json", children: JSON.stringify(dataset) },
        ...claimLds.map((ld) => ({
          type: "application/ld+json",
          children: JSON.stringify(ld),
        })),
        { type: "application/ld+json", children: JSON.stringify(crumbs) },
        { type: "application/ld+json", children: JSON.stringify(speakableLd) },
      ],
    };
  },
  component: ProduktfilmStudyPage,
});

function ProduktfilmStudyPage() {
  return (
    <main className="bg-background text-foreground">
      <Section>
        <div className="mx-auto max-w-3xl space-y-6 py-12">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">
            Branschstudie · Publicerad {PUBLISHED}
          </p>
          <h1 className="text-4xl font-semibold sm:text-5xl">{TITLE}</h1>
          <p className="lede text-lg text-muted-foreground">{DESCRIPTION}</p>
          <AuthorByline authorKey="mattias" date={PUBLISHED} reviewedDate={PUBLISHED} />
          <div className="flex flex-wrap gap-3">
            <Button to="/rapport/reklamfilm-2026">Läs hela rapporten</Button>
            <Button to="/for-journalister" variant="outline">
              För journalister
            </Button>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Nyckeltal — citerbara</h2>
          <ul className="space-y-4">
            {KEY_STATS.map((s) => (
              <li
                key={s.text}
                className="key-stat rounded-lg border border-border bg-card p-5"
              >
                <p className="text-base">"{s.text}"</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Ämne: {s.about} · Källa: Fenny, {PUBLISHED}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-4xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Format & prisspann</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Format</th>
                  <th className="px-4 py-3">Längd</th>
                  <th className="px-4 py-3">Prisspann</th>
                  <th className="px-4 py-3">Användning</th>
                </tr>
              </thead>
              <tbody>
                {FORMATS.map((f) => (
                  <tr key={f.name} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{f.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{f.length}</td>
                    <td className="px-4 py-3 text-muted-foreground">{f.price}</td>
                    <td className="px-4 py-3 text-muted-foreground">{f.use}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Spann avser produktion exkl. media-spend, mätt på 120+ leveranser
            2024–2026. Hämta rådata på{" "}
            <code className="rounded bg-muted px-1.5 py-0.5">
              /api/public/branschstats
            </code>
            .
          </p>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-3 py-8">
          <h2 className="text-2xl font-semibold">Metod</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Urval:</strong> 120+ levererade
              produktfilmsproduktioner för svenska e-handels-, B2B- och
              D2C-varumärken.
            </li>
            <li>
              <strong className="text-foreground">Insamlingsperiod:</strong>{" "}
              januari 2024–maj 2026.
            </li>
            <li>
              <strong className="text-foreground">Mått:</strong> prisspann som
              10:e–90:e percentilen, lead-tid som median från godkänd brief till
              levererad master.
            </li>
            <li>
              <strong className="text-foreground">Ansvarig för datan:</strong>{" "}
              Mattias Björk, VD och ansvarig utgivare.
            </li>
          </ul>
          <p className="text-muted-foreground">
            Datapunkter är aggregerade från Fennys interna leveransdata 2024–2026
            (120+ produktfilmsproduktioner för svenska e-handels-, B2B- och
            D2C-varumärken). Kund-ID:n är borttagna. Prisspann är 10:e–90:e
            percentilen. Lead-tid är median från godkänd brief till levererad
            master. Konverteringssiffror är medianeffekt i A/B-tester på
            produktdetaljsida, mätt 90 dagar efter publicering.
          </p>
          <p className="text-muted-foreground">
            Hela datasetet är öppet under CC BY 4.0. Citera "Fenny — Produktfilm i
            Sverige 2026" med länk till{" "}
            <Link to={PATH} className="underline">
              {abs(PATH)}
            </Link>
            .
          </p>
        </div>
      </Section>
    </main>
  );
}
